import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { EditorComponent, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { normalizeFragment } from '../../../utils/rich-text-html.util';
import {
  RICH_TEXT_EDITOR_INIT,
  TINYMCE_SCRIPT_PATH,
} from './rich-text-editor.config';

/**
 * Universal WYSIWYG pop-out used by {@link RichTextFieldComponent} (and any
 * future rich-text field). It hosts a self-hosted TinyMCE editor inside a
 * PrimeNG dialog and exposes two actions: **Save** (apply, stay open) and
 * **Cancel** (discard + close). Save is disabled until the editor content
 * differs from the saved baseline (dirty tracking), and re-disables after each
 * Save — see {@link dirty} / {@link recomputeDirty}.
 *
 * Contract:
 *   - Inputs `[(visible)]`, `[title]`, `[html]`. `html` is the editing HTML —
 *     callers pass `extractBodyHtml(value)` so a legacy full XHTML document is
 *     unwrapped to its `<body>` before editing.
 *   - Output `(save)` emits the CURRENT editor HTML (a fragment).
 *     `(cancel)` emits nothing. The host owns normalization
 *     (`normalizeFragment`) and writing back to the form control — this dialog
 *     stays sheet-agnostic.
 *
 * TinyMCE is loaded from `/tinymce/tinymce.min.js` (copied from
 * `node_modules/tinymce` by the `angular.json` assets glob), so there is no
 * Tiny Cloud call and no API key. `licenseKey: 'gpl'` selects the community
 * licence appropriate for this internal app.
 */
@Component({
  selector: 'app-rich-text-editor-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    SkeletonModule,
    EditorComponent,
  ],
  // Self-host: load the editor script from our copied assets, never the cloud.
  // Absolute path so it resolves the same from any deep route (e.g. /mbrs/filing).
  providers: [{ provide: TINYMCE_SCRIPT_SRC, useValue: TINYMCE_SCRIPT_PATH }],
  templateUrl: './rich-text-editor-dialog.component.html',
  styleUrls: ['./rich-text-editor-dialog.component.css'],
  // All state changes are input- or event-driven (the `visible`/`html` inputs and
  // the editor/button event bindings in this component's own template). The
  // tinymce-angular outputs emit inside Angular's zone (`ngZone.run`), so those
  // event listeners mark this view dirty — OnPush is safe without manual
  // `markForCheck`.
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RichTextEditorDialogComponent {
  private _visible = false;

  /**
   * Modal visibility (two-way: `[(visible)]`). Every time the dialog opens we
   * reset the working copy back to the source HTML, so any edits left over from
   * a previous **Cancel** are discarded — only **Save** persists. This reset
   * must live here (not only in the `html` setter) because Angular skips the
   * `html` setter when the host re-feeds an unchanged snapshot string.
   */
  @Input()
  set visible(value: boolean) {
    if (value && !this._visible) {
      this.workingHtml = this._sourceHtml;
      // The editor is recreated (`*ngIf`) on every open, so show the skeleton
      // again until TinyMCE re-fires `onInit`.
      this.editorReady = false;
      // Start clean: Save is disabled until the user actually changes content.
      // The real comparison baseline is captured in `onEditorInit` (post-init),
      // because TinyMCE re-serialises the source HTML on load.
      this.dirty = false;
    }
    this._visible = value;
  }
  get visible(): boolean {
    return this._visible;
  }
  @Output() visibleChange = new EventEmitter<boolean>();

  /** Heading shown in the dialog header (usually the field label). */
  @Input() title = 'Edit content';

  /** The source HTML to edit, captured at open time (the discard baseline). */
  private _sourceHtml = '';

  /**
   * The HTML to edit. Set by the host when the dialog opens. The two-way pair
   * is not needed — the host reads the result via the action outputs — but we
   * keep an internal working copy so Cancel can discard cleanly.
   */
  @Input()
  set html(value: string) {
    this._sourceHtml = value ?? '';
    this.workingHtml = this._sourceHtml;
  }

  /** Apply current content but keep editing. */
  @Output() save = new EventEmitter<string>();
  /** Discard changes and close. */
  @Output() cancel = new EventEmitter<void>();

  /** Live editor content, bound to TinyMCE via ngModel. */
  workingHtml = '';

  /**
   * False until TinyMCE finishes initialising. Drives the skeleton overlay that
   * masks the editor's load flash. Reset to `false` on every dialog open.
   */
  editorReady = false;

  /**
   * True when the editor content differs from the saved baseline. Drives the
   * Save button's `[disabled]` state. Recomputed on every editor change and on
   * Save; reset on open. See {@link recomputeDirty}.
   */
  dirty = false;

  /**
   * The **normalized** content the dirty check compares against — the "last
   * saved" snapshot. Captured from the editor **after init** (TinyMCE
   * re-serialises HTML on load, so comparing against the raw source string would
   * flag a false change) and re-pointed on every Save. Mirrors
   * `FilingFormStateService`'s baseline-after-init pattern
   * (`markSheetInitialized`) used by the filing-level Submit gate.
   *
   * Stored already-normalized so {@link recomputeDirty} normalizes only the
   * live content per keystroke instead of re-normalizing this unchanging
   * baseline every time (the regex pass used to run twice per editor event).
   */
  private _normalizedBaseline = '';

  /** The live TinyMCE editor instance, captured at init for content reads. */
  private editorInstance: { getContent: () => string } | null = null;

  /** TinyMCE init — shared self-hosted config (see rich-text-editor.config.ts). */
  readonly editorInit = RICH_TEXT_EDITOR_INIT;

  /**
   * TinyMCE has finished initialising — hide the skeleton and capture the dirty
   * baseline from the editor's own serialisation of the source (so trivial load
   * reformatting is not mistaken for a user edit).
   * Need to revisit on this for optimization
   */
  onEditorInit(event: { editor: { getContent: () => string } }): void {
    this.editorReady = true;
    this.editorInstance = event.editor;
    this._normalizedBaseline = normalizeFragment(event.editor.getContent());
    this.dirty = false;
  }

  /**
   * Recompute whether the editor content differs from the saved baseline.
   * Bound to the editor's change/keyup/command/undo/redo events. The compare is
   * normalised (`normalizeFragment`) so empty variants (`<p></p>`, `&nbsp;`, '')
   * collapse equal — clearing content back to an originally-empty field
   * correctly disables Save.
   */
  recomputeDirty(): void {
    if (!this.editorInstance) return;
    // Only the live content is normalized here; the baseline is already stored
    // normalized (see `_normalizedBaseline`), halving the per-keystroke work.
    this.dirty =
      normalizeFragment(this.editorInstance.getContent()) !==
      this._normalizedBaseline;
  }

  onSave(): void {
    this.save.emit(this.workingHtml);
    // Re-point the baseline to the just-saved content so the next edit is
    // compared against it and Save disables until something changes again.
    this._normalizedBaseline = normalizeFragment(
      this.editorInstance?.getContent() ?? this.workingHtml,
    );
    this.dirty = false;
  }

  onCancel(): void {
    this.cancel.emit();
    this.close();
  }

  /**
   * Programmatic `[(visible)]` close is treated as Cancel. The header X is
   * removed (`[closable]="false"`), so the only close paths are the Cancel /
   * Save footer buttons; this handler stays as a defensive catch-all.
   */
  onVisibleChange(value: boolean): void {
    if (!value) {
      this.cancel.emit();
    }
    this.visible = value;
    this.visibleChange.emit(value);
  }

  private close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
